import { useCallback, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  findNextLevel,
  useStartTrainingAttempt,
  useTrainingSimulatorHome,
  type TrainingAttemptMode,
} from "@/lib/api/training-simulator/training-simulator";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

interface EnterSessionOptions {
  onStarted?: () => void;
}

/**
 * Starts (or retries) an attempt for a level and navigates into its session.
 * Shared by the level row, home header, and completion flow so the
 * start-attempt → navigate handshake lives in exactly one place.
 */
export function useEnterTrainingSession(options?: EnterSessionOptions) {
  const navigate = useNavigate();
  const { track } = useTrackEvent();
  const {
    mutate: startAttempt,
    isPending,
    variables,
  } = useStartTrainingAttempt();

  const enterSession = useCallback(
    (levelId: string, mode: TrainingAttemptMode) => {
      startAttempt(
        { levelId, mode },
        {
          onSuccess: (attempt) => {
            track(
              mode === "retry"
                ? PostHogEvents.SIMULATOR_LEVEL_RETRY
                : PostHogEvents.SIMULATOR_LEVEL_START,
              { level_id: levelId, attempt_id: attempt.id },
            );
            options?.onStarted?.();
            console.log("attempt", attempt);
            navigate({
              to: "/training/session/$attemptId",
              params: { attemptId: attempt.id },
            });
          },
        },
      );
    },
    [navigate, options, startAttempt, track],
  );

  const goToSession = useCallback(
    (attemptId: string) => {
      navigate({
        to: "/training/session/$attemptId",
        params: { attemptId },
      });
    },
    [navigate],
  );

  return {
    enterSession,
    goToSession,
    isStarting: isPending,
    startingMode: isPending ? variables?.mode : undefined,
  };
}

/**
 * Resolves the latest server-owned next level before starting a new attempt.
 * Completion screens can be rendered from stale home-cache data, so the POST
 * target must come from a fresh home response.
 */
export function useContinueToNextTrainingLevel(options?: EnterSessionOptions) {
  const navigate = useNavigate();
  const { track } = useTrackEvent();
  const { refetch: refetchHome } = useTrainingSimulatorHome();
  const { enterSession, startingMode } = useEnterTrainingSession(options);
  const [isResolvingNextLevel, setIsResolvingNextLevel] = useState(false);

  const continueToNextLevel = useCallback(async () => {
    track(PostHogEvents.SIMULATOR_MODULE_CONTINUE_CLICK);
    setIsResolvingNextLevel(true);
    try {
      const { data: latestHome } = await refetchHome();
      const match = findNextLevel(latestHome);
      if (match) {
        enterSession(match.level.id, "start");
      } else {
        navigate({ to: "/training" });
      }
    } finally {
      setIsResolvingNextLevel(false);
    }
  }, [enterSession, navigate, refetchHome, track]);

  return {
    continueToNextLevel,
    isContinuingToNextLevel: isResolvingNextLevel || startingMode === "start",
  };
}
